import React, { useMemo, useState, useEffect, useRef } from 'react';
import { isMobileOnly } from 'react-device-detect';
import { withTranslation } from 'react-i18next';
import { Typography, Hidden } from '@mui/material';

import { useGetActiveRooms } from '@core/dashboard/components/EventDrawer/EventDrawerContent/ParticipantsTab/hooks';
import { useMytaverseEvent } from '@core/dashboard/providers';
import SearchField, {
  useSearchField,
} from '@core/dashboard/components/SearchField';
import ScrollableContainer from '@components/ScrollableContainer';
import { IParticipant } from '@interfaces/participants';
import ParticipantList from './ParticipantList';
import ParticipantNumberInfo from './ParticipantNumberInfo';
import { useStyles } from './styles';
import { IParticipantsContent } from './interfaces';

const ParticipantsContent = ({
  onClose,
  t: translate,
}: IParticipantsContent) => {
  const classes = useStyles();

  const [page, setPage] = useState(1);
  const bottomRef = useRef(null);
  const isBottomVisibleRef = useRef(true);

  const { rooms, participants, currentParticipant } = useMytaverseEvent();

  const fieldsForFilter = useMemo(() => ['name', 'email'], []);

  const { inputValue, filteredData, handleInputChange, handleInputClear } =
    useSearchField<IParticipant>(participants, fieldsForFilter);

  const getActiveRooms = useGetActiveRooms();

  const activeRooms = useMemo(
    () => getActiveRooms(filteredData, rooms),
    [filteredData, rooms, getActiveRooms],
  );

  const participantsList = useMemo(
    () =>
      activeRooms.slice().map((r) => {
        if (r.roomId === 'offline') {
          const activeParticipants = r.activeParticipants.slice(0, 20 * page);
          isBottomVisibleRef.current =
            activeParticipants.length < r.activeParticipants.length;
          return {
            ...r,
            activeParticipants: activeParticipants,
          };
        }

        return r;
      }),
    [activeRooms, page],
  );

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        setPage((prevPage) => ++prevPage);
      }
    });

    if (bottomRef.current) {
      observer.observe(bottomRef.current);
    }

    return () => {
      if (bottomRef.current) observer.unobserve(bottomRef.current);
    };
  }, []);

  return (
    <>
      {!isMobileOnly && <ParticipantNumberInfo />}
      <Hidden mdDown>
        <SearchField
          value={inputValue}
          onChange={handleInputChange}
          onClear={handleInputClear}
          placeholder={translate('inputPlaceholders.sidebarParticipant')}
          sx={classes.search}
        />
      </Hidden>

      <ScrollableContainer sxProps={{ root: classes.scrollable }}>
        {!filteredData.length ? (
          <Typography sx={classes.empty}>
            {translate('participantsModal.searchNoResults')} {inputValue}
          </Typography>
        ) : null}
        {participantsList.map((room) => (
          <ParticipantList
            key={room.roomId}
            name={room.roomId === 'offline' ? translate('sidebar.offline') : ''}
            participants={room.activeParticipants}
            currentParticipantId={currentParticipant?.participantId || ''}
            isOffline={room.roomId === 'offline'}
            onClose={onClose}
          />
        ))}

        {isBottomVisibleRef.current ? (
          <div ref={bottomRef} style={{ height: '4px' }}></div>
        ) : null}
      </ScrollableContainer>
    </>
  );
};

export default withTranslation()(ParticipantsContent);
